import React, { useEffect, useState } from "react";
import api, { formatMoney, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertCircle, Clock, CheckCircle2, Percent, Bell, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("overdue");
  const [vfDialog, setVfDialog] = useState({ open: false, txn: null, pct: 3 });

  async function load() {
    const { data } = await api.get("/summary/upcoming", { params: { days: 120 } });
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  const overdue = items.filter((x) => x.days_diff < 0);
  const today = items.filter((x) => x.days_diff === 0);
  const upcoming = items.filter((x) => x.days_diff > 0);

  async function markPaid(id) {
    try {
      await api.post(`/transactions/${id}/mark-paid`);
      toast.success("Ödeme kaydedildi");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function applyVf() {
    try {
      await api.post(`/transactions/${vfDialog.txn.id}/vade-farki`, { pct_monthly: Number(vfDialog.pct) });
      toast.success(`Vade farkı uygulandı`);
      setVfDialog({ open: false, txn: null, pct: 3 });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function sendReminder(t) {
    (async () => {
      try {
        const { data } = await api.post(`/transactions/${t.id}/send-reminder`);
        toast.success("E-posta hatırlatması gönderildi", {
          description: `${t.company_name} · ${data.sent_to}`,
        });
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }

  function renderRows(rows) {
    if (rows.length === 0)
      return <div className="p-10 text-center text-slate-500">Kayıt bulunamadı</div>;
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02]">
            <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
              <th className="py-3 px-5">Firma</th>
              <th className="py-3 px-5">Açıklama</th>
              <th className="py-3 px-5">Tip</th>
              <th className="py-3 px-5">Vade</th>
              <th className="py-3 px-5">Tutar</th>
              <th className="py-3 px-5">Gecikme</th>
              <th className="py-3 px-5">Vade Farkı</th>
              <th className="py-3 px-5 text-right">Aksiyon</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`payment-row-${t.id}`}>
                <td className="py-3 px-5 font-medium">{t.company_name}</td>
                <td className="py-3 px-5 text-slate-300">{t.description}</td>
                <td className="py-3 px-5">
                  {t.type === "sale" ? (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/25">Alacak</Badge>
                  ) : (
                    <Badge className="bg-red-500/15 text-red-300 border-red-500/25">Borç</Badge>
                  )}
                </td>
                <td className="py-3 px-5 tabular text-slate-300">{formatDate(t.due_date)}</td>
                <td className="py-3 px-5 tabular font-semibold">{formatMoney(t.amount)}</td>
                <td className="py-3 px-5">
                  {t.overdue_days > 0 ? (
                    <span className="text-red-300 tabular font-semibold">{t.overdue_days} gün</span>
                  ) : (
                    <span className="text-slate-500">—</span>
                  )}
                </td>
                <td className="py-3 px-5 tabular text-amber-300">
                  {t.vade_farki_amount > 0 ? formatMoney(t.vade_farki_amount) : t.auto_vade_farki > 0 ? (
                    <span className="text-slate-500">önerilen: {formatMoney(t.auto_vade_farki)}</span>
                  ) : "-"}
                </td>
                <td className="py-3 px-5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {t.overdue_days > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => setVfDialog({ open: true, txn: t, pct: t.vade_farki_pct || 3 })} data-testid={`apply-vf-${t.id}`} className="text-amber-300 hover:bg-amber-500/10">
                        <Percent className="w-3.5 h-3.5 mr-1" /> Vade Farkı
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => sendReminder(t)} data-testid={`remind-${t.id}`} className="hover:bg-blue-500/10 text-blue-300">
                      <Mail className="w-3.5 h-3.5 mr-1" /> Hatırlat
                    </Button>
                    <Button size="sm" onClick={() => markPaid(t.id)} data-testid={`markpaid-${t.id}`} className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Öde
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <header>
        <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Vade Takibi</div>
        <h1 className="text-4xl font-bold tracking-tight mt-1">Ödemeler & Tahsilatlar</h1>
        <p className="text-slate-400 mt-1 text-sm">Vadesi geçen, günü gelen ve yaklaşan ödemeleri tek panelden yönetin.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-elevated rounded-2xl p-5 border-red-500/25 border">
          <div className="flex items-center gap-2 text-red-300 text-xs mono uppercase tracking-widest">
            <AlertCircle className="w-4 h-4" /> Vadesi Geçen
          </div>
          <div className="text-3xl font-bold tabular mt-2">{overdue.length}</div>
          <div className="text-xs text-slate-500">acil aksiyon</div>
        </div>
        <div className="card-elevated rounded-2xl p-5 border-amber-500/25 border">
          <div className="flex items-center gap-2 text-amber-300 text-xs mono uppercase tracking-widest">
            <Bell className="w-4 h-4" /> Bugün Vadesi
          </div>
          <div className="text-3xl font-bold tabular mt-2">{today.length}</div>
          <div className="text-xs text-slate-500">hemen hatırlat</div>
        </div>
        <div className="card-elevated rounded-2xl p-5 border-blue-500/25 border">
          <div className="flex items-center gap-2 text-blue-300 text-xs mono uppercase tracking-widest">
            <Clock className="w-4 h-4" /> Yaklaşan (120 gün)
          </div>
          <div className="text-3xl font-bold tabular mt-2">{upcoming.length}</div>
          <div className="text-xs text-slate-500">takip listesi</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#111723] border border-white/5">
          <TabsTrigger value="overdue" data-testid="tab-overdue">Vadesi Geçen ({overdue.length})</TabsTrigger>
          <TabsTrigger value="today" data-testid="tab-today">Bugün ({today.length})</TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Yaklaşan ({upcoming.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="overdue" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderRows(overdue)}</div></TabsContent>
        <TabsContent value="today" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderRows(today)}</div></TabsContent>
        <TabsContent value="upcoming" className="mt-4"><div className="card-elevated rounded-2xl overflow-hidden">{renderRows(upcoming)}</div></TabsContent>
      </Tabs>

      {/* Vade farkı dialog */}
      <Dialog open={vfDialog.open} onOpenChange={(v) => setVfDialog({ ...vfDialog, open: v })}>
        <DialogContent className="bg-[#0B111C] border-white/10 max-w-md" data-testid="vade-farki-dialog">
          <DialogHeader>
            <DialogTitle>Vade Farkı Uygula</DialogTitle>
          </DialogHeader>
          {vfDialog.txn && (
            <div className="space-y-3">
              <div className="text-sm text-slate-400">{vfDialog.txn.company_name} · {vfDialog.txn.description}</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-[#111723] p-3">
                  <div className="text-[10px] mono uppercase tracking-widest text-slate-500">Anapara</div>
                  <div className="tabular font-bold text-lg">{formatMoney(vfDialog.txn.amount)}</div>
                </div>
                <div className="rounded-lg bg-[#111723] p-3">
                  <div className="text-[10px] mono uppercase tracking-widest text-slate-500">Gecikme</div>
                  <div className="tabular font-bold text-lg text-red-300">{vfDialog.txn.overdue_days} gün</div>
                </div>
              </div>
              <div>
                <Label>Aylık Vade Farkı Oranı (%)</Label>
                <Input type="number" step="0.1" value={vfDialog.pct} onChange={(e) => setVfDialog({ ...vfDialog, pct: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="vade-farki-pct-input" />
                <div className="mt-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
                  <div className="text-[10px] mono uppercase tracking-widest text-amber-400">Hesaplanan Vade Farkı</div>
                  <div className="tabular font-bold text-2xl text-amber-300 mt-1">
                    {formatMoney((vfDialog.txn.amount * (Number(vfDialog.pct) / 100) * (vfDialog.txn.overdue_days / 30)) || 0)}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">= Anapara × (%oran / 100) × (Gecikme / 30)</div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVfDialog({ ...vfDialog, open: false })} className="border-white/10">İptal</Button>
            <Button onClick={applyVf} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="apply-vf-confirm">Uygula</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
